import { ChangeStatusDialog } from "@/components/invoices/ChangeStatusDialog";
import { CopyableValue } from "@/components/invoices/CopyableValue";
import { DeleteInvoiceDialog } from "@/components/invoices/DeleteInvoiceDialog";
import { InvoiceNeighborCard } from "@/components/invoices/InvoiceNeighborCard";
import { InvoicePaymentHistory } from "@/components/invoices/InvoicePaymentHistory";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/Badge";
import { ProviderIcon } from "@/components/ui/ProviderIcon";
import { cn } from "@/lib/utils";
import * as shared from "@/styles/shared";
import type { BadgeVariant } from "@/types/ui";
import { downloadInvoicePdf, openInvoicePdf } from "@/utils/invoiceDownload";
import api from "@shared/api/client";
import type { TranslateFunction } from "@shared/types/i18n";
import type { Status } from "@shared/types/models";
import type { InvoiceAverageComparison, InvoiceDetail } from "@shared/types/payment";
import { compareInvoiceAmountToAverage, daysBetween, daysSince, formatAmount, formatDate, formatDayCount } from "@shared/utils";
import { Ban, Calendar, CreditCard, DollarSign, Download, ExternalLink, Hash, MapPin, Percent, Tag, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import * as styles from "./InvoiceDetailPage.styles";
import { STATUS_BADGE_VARIANTS } from "./InvoicesPage.styles";

const AVERAGE_BADGE: Record<InvoiceAverageComparison, { key: string; variant: BadgeVariant }> = {
  above: { key: "aboveProviderAverage", variant: "warning" },
  matches: { key: "matchesProviderAverage", variant: "secondary" },
  below: { key: "belowProviderAverage", variant: "success" },
};

const paymentDurationText = (invoice: InvoiceDetail, t: TranslateFunction): string => {
  if (!invoice.datePaid) return t("invoiceDetail:outstandingFor", { days: formatDayCount(daysSince(invoice.invoiceDate), t) });
  const paidInDays = daysBetween(invoice.invoiceDate, invoice.datePaid);
  if (!paidInDays) return t("invoiceDetail:today");
  return t("invoiceDetail:paidInDays", { days: formatDayCount(paidInDays, t) });
};

const InvoiceDetailPage = () => {
  const { t } = useTranslation(["common", "invoiceDetail", "status"]);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchInvoice = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await api.invoices.get(parseInt(id, 10));
      setInvoice(data);
    } catch (error) {
      console.error("Failed to fetch invoice:", error);
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void fetchInvoice();
  }, [fetchInvoice]);

  const handleDownload = () => {
    if (!invoice) return;
    void downloadInvoicePdf(invoice.id);
  };

  const handleOpenPdf = () => {
    if (!invoice) return;
    void openInvoicePdf(invoice.id);
  };

  const handleStatusChange = async (status: Status) => {
    if (!invoice) return;
    try {
      const updated = await api.invoices.updateStatus(invoice.id, status);
      setInvoice(updated);
    } finally {
      setShowStatusDialog(false);
    }
  };

  const handleDelete = async () => {
    if (!invoice) return;
    setDeleting(true);
    try {
      await api.invoices.remove(invoice.id);
      navigate("/invoices");
    } catch (error) {
      console.error("Failed to delete invoice:", error);
      setShowDeleteDialog(false);
    } finally {
      setDeleting(false);
    }
  };

  if (loading)
    return (
      <div className={shared.page}>
        <div className={styles.loadingSkeleton} />
      </div>
    );
  if (!invoice) return <div className={shared.page} />;

  const statusVariant = STATUS_BADGE_VARIANTS[invoice.status];
  const { addressProvider } = invoice;
  const providerName = addressProvider.provider.name;
  const averageComparison = compareInvoiceAmountToAverage(invoice.amount, invoice.providerAverageAmount);

  return (
    <div className={shared.page}>
      <PageHeader
        title={t("pageTitle", { ns: "invoiceDetail", id: invoice.id })}
        onBack={() => navigate("/invoices")}
        backLabel={t("backLink", { ns: "invoiceDetail" })}
      />

      <div className={styles.card}>
        <dl className={styles.detailGrid}>
          <div className={styles.detailRow}>
            <Calendar className={styles.detailIcon} />
            <div>
              <dt className={styles.detailLabel}>{t("detail.date")}</dt>
              <dd className={shared.titleSmSemibold}>{formatDate(invoice.invoiceDate)}</dd>
            </div>
          </div>

          <div className={styles.detailRow}>
            <DollarSign className={styles.detailIcon} />
            <div>
              <dt className={styles.detailLabel}>{t("detail.amount")}</dt>
              <dd className={shared.titleSmSemibold}>{formatAmount(invoice.amount)}</dd>
            </div>
          </div>

          <div className={styles.detailRow}>
            <Tag className={styles.detailIcon} />
            <div>
              <dt className={styles.detailLabel}>{t("detail.status")}</dt>
              <dd className={styles.statusBadgeWrapper}>
                <Badge variant={statusVariant}>{t(invoice.status, { ns: "status" })}</Badge>
              </dd>
            </div>
          </div>

          {invoice.datePaid && (
            <div className={styles.detailRow}>
              <Calendar className={styles.detailIcon} />
              <div>
                <dt className={styles.detailLabel}>{t("detail.datePaid")}</dt>
                <dd className={shared.titleSmSemibold}>{formatDate(invoice.datePaid)}</dd>
              </div>
            </div>
          )}

          <div className={styles.detailRow}>
            <MapPin className={styles.detailIcon} />
            <div>
              <dt className={styles.detailLabel}>{t("detail.address")}</dt>
              <dd className={shared.titleSmSemibold}>{addressProvider.address.name}</dd>
            </div>
          </div>

          <div className={styles.detailRow}>
            <ProviderIcon provider={providerName} size="md" className={styles.detailIcon} />
            <div>
              <dt className={styles.detailLabel}>{t("detail.provider")}</dt>
              <dd className={shared.titleSmSemibold}>{providerName}</dd>
            </div>
          </div>

          {invoice.reference && (
            <div className={styles.detailRow}>
              <Hash className={styles.detailIcon} />
              <div>
                <dt className={styles.detailLabel}>{t("detail.reference")}</dt>
                <dd className={shared.titleSmSemibold}>
                  <CopyableValue value={invoice.reference} />
                </dd>
              </div>
            </div>
          )}

          {invoice.account && (
            <div className={styles.detailRow}>
              <CreditCard className={styles.detailIcon} />
              <div>
                <dt className={styles.detailLabel}>{t("detail.account")}</dt>
                <dd className={shared.titleSmSemibold}>
                  <CopyableValue value={invoice.account} />
                </dd>
              </div>
            </div>
          )}
        </dl>

        {invoice.address && (
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>{t("sections.billing", { ns: "invoiceDetail" })}</h2>
            <div className={styles.valueRow}>
              <span className={styles.valueRowLabel}>{t("field.providerAddress", { ns: "invoiceDetail" })}</span>
              <span className={styles.valueRowValue}>{invoice.address}</span>
            </div>
          </div>
        )}

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>{t("sections.account", { ns: "invoiceDetail" })}</h2>

          {addressProvider.username && (
            <div className={styles.valueRow}>
              <span className={styles.valueRowLabel}>{t("field.username", { ns: "invoiceDetail" })}</span>
              <span className={styles.valueRowValue}>
                <CopyableValue value={addressProvider.username} />
              </span>
            </div>
          )}

          {addressProvider.accountKeyword && (
            <div className={styles.valueRow}>
              <span className={styles.valueRowLabel}>{t("field.accountKeyword", { ns: "invoiceDetail" })}</span>
              <span className={styles.valueRowValue}>
                <CopyableValue value={addressProvider.accountKeyword} />
              </span>
            </div>
          )}

          <div className={styles.valueRow}>
            <span className={styles.valueRowLabel}>{t("field.password", { ns: "invoiceDetail" })}</span>
            <span className={styles.valueRowValue}>{addressProvider.password ?? t("notSet", { ns: "common" })}</span>
          </div>

          {addressProvider.taxDeduction && addressProvider.taxDeductionAmount !== null && (
            <div className={styles.valueRow}>
              <span className={styles.valueRowLabel}>{t("field.taxDeductionAmount", { ns: "invoiceDetail" })}</span>
              <span className={styles.valueRowValue}>{formatAmount(addressProvider.taxDeductionAmount)}</span>
            </div>
          )}

          <div className={styles.flagRow}>
            <span className={`${styles.flagChip} ${addressProvider.taxDeduction ? "" : styles.flagChipMuted}`}>
              <Percent className={styles.copyIcon} />
              {t("field.taxDeduction", { ns: "invoiceDetail" })}
              <span className={styles.flagChipMuted}>
                {" — "}
                {addressProvider.taxDeduction ? t("enabled", { ns: "invoiceDetail" }) : t("disabled", { ns: "invoiceDetail" })}
              </span>
            </span>

            <span className={cn(styles.flagChip, addressProvider.skipPayment ? styles.flagChipWarning : styles.flagChipMuted)}>
              <Ban className={styles.copyIcon} />
              {t("field.skipPayment", { ns: "invoiceDetail" })}
              <span className={styles.flagChipMuted}>
                {" — "}
                {addressProvider.skipPayment ? t("skipped", { ns: "invoiceDetail" }) : t("enabled", { ns: "invoiceDetail" })}
              </span>
            </span>
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>{t("sections.context", { ns: "invoiceDetail" })}</h2>

          <div className={styles.valueRow}>
            <span className={styles.valueRowLabel}>{t("field.createdAt", { ns: "invoiceDetail" })}</span>
            <span className={styles.valueRowValue}>{formatDate(invoice.createdAt)}</span>
          </div>

          <div className={styles.valueRow}>
            <span className={styles.valueRowLabel}>{t("field.updatedAt", { ns: "invoiceDetail" })}</span>
            <span className={styles.valueRowValue}>{formatDate(invoice.updatedAt)}</span>
          </div>

          <div className={styles.valueRow}>
            <span className={styles.valueRowLabel}>
              {invoice.datePaid ? t("field.paymentDuration", { ns: "invoiceDetail" }) : t("field.age", { ns: "invoiceDetail" })}
            </span>
            <span className={styles.valueRowValue}>{paymentDurationText(invoice, t)}</span>
          </div>

          {invoice.providerAverageAmount !== null && (
            <div className={styles.valueRow}>
              <span className={styles.valueRowLabel}>{t("field.providerAverage", { ns: "invoiceDetail" })}</span>
              <span className={styles.valueRowValue}>{formatAmount(invoice.providerAverageAmount)}</span>
            </div>
          )}

          {invoice.addressAverageAmount !== null && (
            <div className={styles.valueRow}>
              <span className={styles.valueRowLabel}>{t("field.addressAverage", { ns: "invoiceDetail" })}</span>
              <span className={styles.valueRowValue}>{formatAmount(invoice.addressAverageAmount)}</span>
            </div>
          )}

          {averageComparison && (
            <div className={styles.flagRow}>
              <Badge variant={AVERAGE_BADGE[averageComparison].variant}>
                {t(AVERAGE_BADGE[averageComparison].key, { ns: "invoiceDetail", average: formatAmount(invoice.providerAverageAmount) })}
              </Badge>
            </div>
          )}
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>{t("sections.neighbors", { ns: "invoiceDetail" })}</h2>
          <div className={styles.neighborGrid}>
            <InvoiceNeighborCard neighbor={invoice.previousInvoice} direction="previousInvoice" />
            <InvoiceNeighborCard neighbor={invoice.nextInvoice} direction="nextInvoice" />
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>{t("sections.paymentHistory", { ns: "invoiceDetail" })}</h2>
          <InvoicePaymentHistory attempts={invoice.paymentHistory} />
        </div>

        <div className={styles.buttonGroup}>
          <button onClick={() => setShowStatusDialog(true)} className={styles.primaryButton}>
            {t("changeStatusButton", { ns: "invoiceDetail" })}
          </button>
          {invoice.fileKey && (
            <>
              <button onClick={handleDownload} className={styles.secondaryButton}>
                <Download className={shared.iconSm} />
                {t("downloadPdf", { ns: "invoiceDetail" })}
              </button>
              <button onClick={handleOpenPdf} className={styles.secondaryButton}>
                <ExternalLink className={shared.iconSm} />
                {t("openPdf", { ns: "invoiceDetail" })}
              </button>
            </>
          )}
          <button onClick={() => setShowDeleteDialog(true)} className={styles.secondaryButton}>
            <Trash2 className={shared.iconSm} />
            {t("deleteInvoice", { ns: "invoiceDetail" })}
          </button>
        </div>
      </div>

      <ChangeStatusDialog
        isOpen={showStatusDialog}
        invoice={invoice}
        onStatusChange={handleStatusChange}
        onClose={() => setShowStatusDialog(false)}
      />

      <DeleteInvoiceDialog
        isOpen={showDeleteDialog}
        deleting={deleting}
        title={t("deleteTitle", { ns: "invoiceDetail" })}
        message={t("deleteMessage", { ns: "invoiceDetail", id: invoice.id })}
        onConfirm={handleDelete}
        onClose={() => setShowDeleteDialog(false)}
      />
    </div>
  );
};

export default InvoiceDetailPage;
